import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Users, Plus } from "lucide-react";
import { useGameStore } from "@/lib/gameStore";
import { generateRoomCode } from "@/lib/gameUtils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validatePlayerName } from "@/lib/validationSchemas";
import { mapDatabaseError } from "@/lib/errorUtils";

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface MenuScreenProps {
  onNavigate: (screen: "lobby" | "join") => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function MenuScreen({ onNavigate, setMusicIntensity }: MenuScreenProps) {
  // Set calm music intensity when on menu
  useEffect(() => {
    setMusicIntensity('calm');
  }, [setMusicIntensity]);
  const { playerName, setPlayerName, setPlayerId, setCurrentRoom } = useGameStore();
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const handleCreateRoom = async () => {
    // Validate player name
    const nameError = validatePlayerName(playerName);
    if (nameError) {
      toast({ title: "Invalid name", description: nameError, variant: "destructive" });
      return;
    }

    setIsCreating(true);
    try {
      // Sign in anonymously to get auth_user_id
      const { data: authData, error: authError } = await supabase.auth.signInAnonymously();
      if (authError) throw authError;

      const authUserId = authData.user.id;
      const roomCode = generateRoomCode();

      // Create room in database with auth_user_id
      const { error: roomError } = await supabase.from("rooms").insert({
        id: roomCode,
        host_id: "temp",
        status: "lobby",
        auth_user_id: authUserId,
      });

      if (roomError) throw roomError;

      // Create player with auth_user_id
      const { data: player, error: playerError } = await supabase
        .from("players")
        .insert({
          room_id: roomCode,
          name: playerName,
          is_host: true,
          auth_user_id: authUserId,
        })
        .select()
        .single();

      if (playerError) throw playerError;

      // Update room with actual host_id
      await supabase.from("rooms").update({ host_id: player.id }).eq("id", roomCode);

      setPlayerId(player.id);
      setCurrentRoom({
        id: roomCode,
        hostId: player.id,
        status: "lobby",
        currentRound: 0,
        currentTotal: 0,
        targetTotal: 67,
        players: [
          {
            id: player.id,
            name: playerName,
            isHost: true,
            stressLevel: "calm",
            isActive: true,
          },
        ],
        hasSelected: false,
        roundTimeRemaining: 3,
      });

      onNavigate("lobby");
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error creating room (dev only):", error);
      }
      toast({
        title: "Failed to create room",
        description: mapDatabaseError(error),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background" />
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md space-y-8"
      >
        {/* Hero Logo */}
        <div className="text-center space-y-4">
          <motion.h1
            className="text-7xl md:text-8xl font-black uppercase tracking-tighter text-shadow"
            animate={{
              textShadow: [
                "0 0 20px hsl(160 84% 39% / 0.5)",
                "0 0 40px hsl(160 84% 39% / 0.8)",
                "0 0 20px hsl(160 84% 39% / 0.5)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="inline-flex items-baseline">
              <span className="text-primary translate-y-[-0.2em]">6</span>
              <span className="text-primary">7</span>
            </span>{" "}
            <span className="text-foreground">CHAOS</span>
          </motion.h1>
          <p className="text-muted-foreground text-lg font-semibold">Hit exactly 67. Together. Or lose trying.</p>
        </div>

        {/* Name Input Card */}
        <Card className="glass-card p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Your Name</label>
            <Input
              value={playerName}
              onChange={(e) => {
                const value = e.target.value.slice(0, 20);
                // Only allow alphanumeric and spaces
                const filtered = value.replace(/[^a-zA-Z0-9 ]/g, "");
                setPlayerName(filtered);
              }}
              placeholder="Enter name (max 20 chars)"
              className="bg-secondary/50 border-2 border-border/50 h-14 text-lg font-semibold"
              maxLength={20}
            />
          </div>

          <div className="grid gap-3 pt-2">
            <Button
              onClick={handleCreateRoom}
              disabled={isCreating || !playerName.trim()}
              className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform"
            >
              <Plus className="mr-2 h-6 w-6" />
              Create Room
            </Button>

            <Button
              onClick={() => onNavigate("join")}
              disabled={!playerName.trim()}
              variant="secondary"
              className="h-16 text-lg font-bold uppercase tracking-wide hover:scale-105 transition-transform"
            >
              <Users className="mr-2 h-6 w-6" />
              Join Room
            </Button>
          </div>
        </Card>

        {/* How to Play */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center space-y-2 text-sm text-muted-foreground"
        >
          <p className="font-semibold">🎯 Pick numbers each round</p>
          <p className="font-semibold">🔴🟡🟢 Colors guide you to 67</p>
          <p className="font-semibold">💀 Work together or chaos wins</p>
        </motion.div>
      </motion.div>
    </div>
  );
}
